import { Link } from "react-router-dom";
import { useState } from "react";
import { Sparkles, X, ArrowRight, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const HeroExplosiveBanner = () => {
  const { getSetting } = useSiteSettings();
  const [dismissed, setDismissed] = useState(false);

  const enabled = getSetting("hero_explosive_enabled", "false") === "true";
  const title = getSetting("hero_explosive_title", "🚀 LANCEMENT OFFICIEL");
  const subtitle = getSetting(
    "hero_explosive_subtitle",
    "La plateforme de garde d'enfants nouvelle génération arrive — rejoignez l'aventure !"
  );
  const ctaText = getSetting("hero_explosive_cta_text", "Je m'inscris");
  const ctaLink = getSetting("hero_explosive_cta_link", "/auth?tab=register&role=parent");
  const titleColor = getSetting("hero_explosive_title_color", "#FDE047");
  const subtitleColor = getSetting("hero_explosive_subtitle_color", "#FFFFFF");
  const ctaBgColor = getSetting("hero_explosive_cta_bg_color", "#FFFFFF");
  const ctaTextColor = getSetting("hero_explosive_cta_text_color", "#7B2FFF");
  const fontFamily = getSetting("hero_explosive_font_family", "display");
  const neonTitle = getSetting("hero_explosive_neon_title", "true") === "true";
  const neonSubtitle = getSetting("hero_explosive_neon_subtitle", "false") === "true";
  const neonCta = getSetting("hero_explosive_neon_cta", "true") === "true";
  const neonColor = getSetting("hero_explosive_neon_color", "#FF4D9D");

  const fontClassMap: Record<string, string> = {
    display: "font-display",
    sans: "font-sans",
    serif: "font-serif",
    mono: "font-mono",
  };
  const fontClass = fontClassMap[fontFamily] || "font-display";

  const neonGlow = (color: string) =>
    `0 0 6px ${color}, 0 0 12px ${color}, 0 0 24px ${color}, 0 0 48px ${color}`;

  if (!enabled || dismissed) return null;

  return (
    <div className="relative overflow-hidden hero-explosive">
      {/* Animated radial bursts */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="hero-explosive-burst hero-explosive-burst-1" />
        <div className="hero-explosive-burst hero-explosive-burst-2" />
        <div className="hero-explosive-burst hero-explosive-burst-3" />
      </div>

      {/* Sparkle particles */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 14 }).map((_, i) => (
          <span
            key={i}
            className="hero-explosive-sparkle"
            style={{
              left: `${(i * 73) % 100}%`,
              top: `${(i * 41) % 100}%`,
              animationDelay: `${(i % 7) * 0.3}s`,
            }}
          />
        ))}
      </div>

      <div className="relative px-4 md:px-6 py-6 md:py-8 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 text-center md:text-left">
        <div className="hero-explosive-icon shrink-0">
          <Rocket className="w-7 h-7 md:w-9 md:h-9 text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
            <Sparkles className="w-4 h-4 animate-pulse" style={{ color: titleColor }} />
            <span
              className={`text-xs md:text-sm font-black tracking-[0.2em] uppercase ${fontClass}`}
              style={{
                color: titleColor,
                textShadow: neonTitle ? neonGlow(neonColor) : "0 0 10px rgba(0,0,0,0.4)",
              }}
            >
              {title}
            </span>
            <Sparkles className="w-4 h-4 animate-pulse" style={{ color: titleColor }} />
          </div>
          <p
            className={`text-xl md:text-3xl lg:text-4xl font-black leading-tight ${fontClass}`}
            style={{
              color: subtitleColor,
              textShadow: neonSubtitle ? neonGlow(neonColor) : "0 2px 20px rgba(0,0,0,0.5)",
            }}
          >
            {subtitle}
          </p>
        </div>

        <Link to={ctaLink} className="shrink-0">
          <Button
            size="lg"
            className={`rounded-full h-12 md:h-14 px-6 md:px-8 font-black text-base md:text-lg hover:scale-105 transition-transform ${fontClass}`}
            style={{
              backgroundColor: ctaBgColor,
              color: ctaTextColor,
              boxShadow: neonCta
                ? `0 0 20px ${neonColor}, 0 0 40px ${neonColor}, 0 0 60px ${neonColor}`
                : "0 0 40px rgba(255,255,255,0.5)",
            }}
          >
            {ctaText}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>

        <button
          onClick={() => setDismissed(true)}
          aria-label="Fermer la bannière"
          className="absolute top-2 right-2 text-white/70 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default HeroExplosiveBanner;
